import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, Box, Typography, IconButton, Divider,
  FormControlLabel, Switch,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import dayjs from 'dayjs';
import { createCampaign, updateCampaign } from '../../api/incentiveApi';
import { uploadNoticeImage } from '../../api/noticeApi';
import { getAddons } from '../../api/resourceApi';

const EMPTY_TIER = { tierName: '', target: '', reward: '', rewardType: 'Cash', rewardAmount: '', sortOrder: '' };
const EMPTY_MULTIPLIER = { startDate: null, endDate: null, multiplier: '' };

const FONT = { fontFamily: "'Poppins', sans-serif" };

export default function CreateCampaignDialog({ open, onClose, onSaved, campaign }) {
  const isEdit = !!campaign;

  const [form, setForm] = useState({
    name: '',
    type: 'Case',
    metric: 'Cases',
    description: '',
    imageUrl: '',
    youTubeUrl: '',
    startDate: null,
    endDate: null,
    tiers: [{ ...EMPTY_TIER, sortOrder: '1' }],
    multipliers: [],
    maxDownlineLevels: '',
    minSalesForDownline: '',
    maxDownlineContribution: '',
    downlineContributionPercent: '20',
    includeAddOnInRevenue: false,
    minEligibility: '',
    maxEligibility: '',
    addonTarget: '',
    addonMinPrice: '',
  });
  const [saving, setSaving] = useState(false);
  const [addonOptions, setAddonOptions] = useState([]);

  useEffect(() => {
    if (campaign) {
      setForm({
        name: campaign.name ?? '',
        type: campaign.type ?? 'Case',
        metric: campaign.metric ?? 'Cases',
        description: campaign.description ?? '',
        imageUrl: campaign.imageUrl ?? '',
        youTubeUrl: campaign.youTubeUrl ?? '',
        startDate: campaign.startDate ? dayjs(campaign.startDate) : null,
        endDate: campaign.endDate ? dayjs(campaign.endDate) : null,
        tiers: campaign.tiers?.length
          ? campaign.tiers.map((t) => ({
              tierName: t.tierName ?? '',
              target: t.target ?? '',
              reward: t.reward ?? '',
              rewardType: t.rewardType ?? 'Cash',
              rewardAmount: t.rewardAmount ?? '',
              sortOrder: t.sortOrder ?? '',
            }))
          : [{ ...EMPTY_TIER, sortOrder: '1' }],
        multipliers: campaign.multipliers?.length
          ? campaign.multipliers.map((m) => ({
              startDate: m.startDate ? dayjs(m.startDate) : null,
              endDate: m.endDate ? dayjs(m.endDate) : null,
              multiplier: m.multiplier ?? '',
            }))
          : [],
        maxDownlineLevels: campaign.maxDownlineLevels ?? '',
        minSalesForDownline: campaign.minSalesForDownline ?? '',
        maxDownlineContribution: campaign.maxDownlineContribution ?? '',
        downlineContributionPercent: campaign.downlineContributionPercent ?? '20',
        includeAddOnInRevenue: !!campaign.includeAddOnInRevenue,
        minEligibility: campaign.minEligibility ?? '',
        maxEligibility: campaign.maxEligibility ?? '',
        addonTarget: campaign.addonTarget ?? '',
        addonMinPrice: campaign.addonMinPrice ?? '',
      });
    } else {
      setForm({
        name: '',
        type: 'Case',
        metric: 'Cases',
        description: '',
        startDate: null,
        endDate: null,
        tiers: [{ ...EMPTY_TIER, sortOrder: '1' }],
        multipliers: [],
        maxDownlineLevels: '',
        minSalesForDownline: '',
        maxDownlineContribution: '',
        downlineContributionPercent: '20',
        includeAddOnInRevenue: false,
        minEligibility: '',
        maxEligibility: '',
        addonTarget: '',
        addonMinPrice: '',
      });
    }
  }, [campaign, open]);

  // Load add-on catalog for the Addon picker
  useEffect(() => {
    getAddons().then((res) => {
      setAddonOptions(res.data ?? []);
    }).catch(() => {});
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // Auto-set metric for Case and Travel types
      if (field === 'type') {
        if (value === 'Case') next.metric = 'Cases';
        else if (value === 'Travel') next.metric = 'Revenue';
      }
      // Clear add-on fields when metric changes away from Addon
      if (field === 'metric' && value !== 'Addon') {
        next.addonTarget = '';
        next.addonMinPrice = '';
      }
      return next;
    });
  };

  // Tier handlers
  const handleTierChange = (idx, field, value) => {
    setForm((prev) => {
      const tiers = [...prev.tiers];
      tiers[idx] = { ...tiers[idx], [field]: value };
      return { ...prev, tiers };
    });
  };
  const addTier = () => setForm((prev) => ({ ...prev, tiers: [...prev.tiers, { ...EMPTY_TIER, sortOrder: String(prev.tiers.length + 1) }] }));
  const removeTier = (idx) => setForm((prev) => ({ ...prev, tiers: prev.tiers.filter((_, i) => i !== idx) }));

  // Multiplier handlers
  const handleMultiplierChange = (idx, field, value) => {
    setForm((prev) => {
      const multipliers = [...prev.multipliers];
      multipliers[idx] = { ...multipliers[idx], [field]: value };
      return { ...prev, multipliers };
    });
  };
  const addMultiplier = () => setForm((prev) => ({ ...prev, multipliers: [...prev.multipliers, { ...EMPTY_MULTIPLIER }] }));
  const removeMultiplier = (idx) => setForm((prev) => ({ ...prev, multipliers: prev.multipliers.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...(isEdit && { campaignId: campaign.campaignId }),
        name: form.name,
        type: form.type,
        metric: form.metric,
        description: form.description,
        imageUrl: form.imageUrl || null,
        youTubeUrl: form.youTubeUrl || null,
        startDate: form.startDate?.format('YYYY-MM-DD') ?? null,
        endDate: form.endDate?.format('YYYY-MM-DD') ?? null,
        maxDownlineLevels: form.maxDownlineLevels ? Number(form.maxDownlineLevels) : 0,
        minDownlineSales: form.minSalesForDownline ? Number(form.minSalesForDownline) : 0,
        maxDownlineContribution: form.maxDownlineContribution ? Number(form.maxDownlineContribution) : null,
        downlineContributionPercent: form.downlineContributionPercent !== '' ? Number(form.downlineContributionPercent) : 20,
        includeAddOnInRevenue: !!form.includeAddOnInRevenue,
        minEligibility: form.minEligibility !== '' ? Number(form.minEligibility) : null,
        maxEligibility: form.maxEligibility !== '' ? Number(form.maxEligibility) : null,
        addonTarget: form.metric === 'Addon' && form.addonTarget ? form.addonTarget : null,
        addonMinPrice: form.metric === 'Addon' && form.addonMinPrice !== '' ? Number(form.addonMinPrice) : null,
        tiers: form.tiers.map((t) => ({
          tierName: t.tierName,
          target: Number(t.target) || 0,
          reward: t.reward,
          rewardType: t.rewardType || 'Cash',
          rewardAmount: t.rewardType === 'Cash' && t.rewardAmount !== '' ? Number(t.rewardAmount) : null,
          sortOrder: Number(t.sortOrder) || 0,
        })),
        multipliers: form.multipliers.map((m) => ({
          startDate: m.startDate?.format('YYYY-MM-DD') ?? null,
          endDate: m.endDate?.format('YYYY-MM-DD') ?? null,
          multiplier: Number(m.multiplier) || 1,
        })),
      };

      if (isEdit) {
        await updateCampaign(payload);
      } else {
        await createCampaign(payload);
      }
      onSaved?.();
    } catch (err) {
      console.error('Error saving campaign:', err);
      alert('Failed to save campaign: ' + (err.response?.data?.message ?? err.message));
    } finally {
      setSaving(false);
    }
  };

  const sectionTitle = (text) => (
    <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#0F172A', ...FONT, mt: 2, mb: 1 }}>
      {text}
    </Typography>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ fontWeight: 700, color: '#0F172A', ...FONT }}>
        {isEdit ? 'Edit Campaign' : 'Create Campaign'}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 3 }}>
        {/* Basic Info */}
        {sectionTitle('Basic Information')}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1 }}>
          <TextField
            label="Campaign Name"
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            size="small"
            fullWidth
            InputProps={{ sx: FONT }}
            InputLabelProps={{ sx: FONT }}
          />
          <FormControl size="small" fullWidth>
            <InputLabel sx={FONT}>Type</InputLabel>
            <Select value={form.type} label="Type" onChange={(e) => handleChange('type', e.target.value)} sx={FONT}>
              <MenuItem value="Case">Case</MenuItem>
              <MenuItem value="Travel">Travel</MenuItem>
              <MenuItem value="Item">Item</MenuItem>
            </Select>
          </FormControl>
          {form.type === 'Item' ? (
            <FormControl size="small" fullWidth>
              <InputLabel sx={FONT}>Metric</InputLabel>
              <Select value={form.metric} label="Metric" onChange={(e) => handleChange('metric', e.target.value)} sx={FONT}>
                <MenuItem value="Cases">Cases</MenuItem>
                <MenuItem value="Revenue">Revenue</MenuItem>
                <MenuItem value="kWp">kWp</MenuItem>
                <MenuItem value="Addon">Add-on units</MenuItem>
              </Select>
            </FormControl>
          ) : (
            <TextField
              label="Metric"
              value={form.metric}
              size="small"
              fullWidth
              disabled
              helperText="Auto-set based on type"
              InputProps={{ sx: FONT }}
              InputLabelProps={{ sx: FONT }}
            />
          )}
        </Box>

        {/* Add-on Metric config — shown only when metric === 'Addon' */}
        {form.metric === 'Addon' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1, p: 1.5, border: '1px solid #E2E8F0', borderRadius: '8px', backgroundColor: '#F8FAFC' }}>
            <FormControl size="small" fullWidth>
              <InputLabel sx={FONT}>Add-on to count</InputLabel>
              <Select
                value={form.addonTarget}
                label="Add-on to count"
                onChange={(e) => handleChange('addonTarget', e.target.value)}
                sx={FONT}
              >
                <MenuItem value="Battery"><em>Battery (by category)</em></MenuItem>
                {addonOptions.map((a) => (
                  <MenuItem key={a.addonId ?? a.itemName ?? a.name} value={a.itemName ?? a.name}>
                    {a.itemName ?? a.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Minimum unit price (RM) — optional"
              type="number"
              value={form.addonMinPrice}
              onChange={(e) => handleChange('addonMinPrice', e.target.value)}
              size="small"
              fullWidth
              helperText="Only count line items AT OR ABOVE this price (i.e. sold at proper commission). Leave blank for no floor."
              InputProps={{ sx: FONT }}
              InputLabelProps={{ sx: FONT }}
            />
          </Box>
        )}

        <TextField
          label="Description / Terms & Conditions"
          value={form.description}
          onChange={(e) => handleChange('description', e.target.value)}
          multiline
          rows={3}
          fullWidth
          size="small"
          sx={{ mb: 1 }}
          InputProps={{ sx: FONT }}
          InputLabelProps={{ sx: FONT }}
        />
        <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
          <Button variant="outlined" component="label" size="small" sx={{ ...FONT, textTransform: 'none' }}>
            Upload Image
            <input type="file" hidden accept="image/*" onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;
              try {
                const res = await uploadNoticeImage(file);
                const fullUrl = (process.env.REACT_APP_API_BASE_URL?.replace(/\/api$/, '') || '') + res.data.url;
                handleChange('imageUrl', fullUrl);
              } catch { alert('Upload failed'); }
            }} />
          </Button>
          {form.imageUrl && <img src={form.imageUrl} alt="preview" style={{ height: 40, borderRadius: 6 }} />}
          {form.imageUrl && <Button size="small" color="error" onClick={() => handleChange('imageUrl', '')} sx={{ ...FONT, textTransform: 'none', minWidth: 0 }}>Remove</Button>}
        </Box>
        <TextField
          label="YouTube URL"
          value={form.youTubeUrl}
          onChange={(e) => handleChange('youTubeUrl', e.target.value)}
          fullWidth size="small" sx={{ mb: 1 }}
          placeholder="https://youtube.com/watch?v=..."
          InputProps={{ sx: FONT }} InputLabelProps={{ sx: FONT }}
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 1 }}>
          <DatePicker
            label="Start Date"
            value={form.startDate}
            onChange={(val) => handleChange('startDate', val)}
            format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', fullWidth: true, InputProps: { sx: FONT }, InputLabelProps: { sx: FONT } } }}
          />
          <DatePicker
            label="End Date"
            value={form.endDate}
            onChange={(val) => handleChange('endDate', val)}
            format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', fullWidth: true, InputProps: { sx: FONT }, InputLabelProps: { sx: FONT } } }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Tiers */}
        {sectionTitle('Tiers')}
        {form.tiers.map((tier, idx) => (
          <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2, p: 1.5, border: '1px solid #E2E8F0', borderRadius: '8px' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField label="Tier Name" value={tier.tierName} onChange={(e) => handleTierChange(idx, 'tierName', e.target.value)} size="small" sx={{ flex: 2 }} InputProps={{ sx: FONT }} InputLabelProps={{ sx: FONT }} />
              <TextField label="Target" type="number" value={tier.target} onChange={(e) => handleTierChange(idx, 'target', e.target.value)} size="small" sx={{ flex: 1 }} InputProps={{ sx: FONT }} InputLabelProps={{ sx: FONT }} />
              <TextField label="Order" type="number" value={tier.sortOrder} onChange={(e) => handleTierChange(idx, 'sortOrder', e.target.value)} size="small" sx={{ width: 70 }} InputProps={{ sx: FONT }} InputLabelProps={{ sx: FONT }} />
              <IconButton onClick={() => removeTier(idx)} size="small" color="error" disabled={form.tiers.length <= 1}>
                <RemoveCircleOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl size="small" sx={{ width: 130 }}>
                <InputLabel sx={FONT}>Reward Type</InputLabel>
                <Select label="Reward Type" value={tier.rewardType || 'Cash'} onChange={(e) => handleTierChange(idx, 'rewardType', e.target.value)} sx={FONT}>
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Item">Item</MenuItem>
                  <MenuItem value="Travel">Travel</MenuItem>
                </Select>
              </FormControl>
              {tier.rewardType === 'Cash' && (
                <TextField
                  label="Amount (RM)"
                  type="number"
                  value={tier.rewardAmount}
                  onChange={(e) => handleTierChange(idx, 'rewardAmount', e.target.value)}
                  size="small"
                  sx={{ width: 130 }}
                  InputProps={{ sx: FONT }}
                  InputLabelProps={{ sx: FONT }}
                  placeholder="1500"
                />
              )}
              <TextField
                label={tier.rewardType === 'Cash' ? 'Display Label' : tier.rewardType === 'Travel' ? 'Destination' : 'Description'}
                value={tier.reward}
                onChange={(e) => handleTierChange(idx, 'reward', e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                InputProps={{ sx: FONT }}
                InputLabelProps={{ sx: FONT }}
                placeholder={tier.rewardType === 'Cash' ? 'RM 1,500' : tier.rewardType === 'Travel' ? 'Bali 4D3N' : 'iPad Pro'}
              />
            </Box>
          </Box>
        ))}
        <Button onClick={addTier} startIcon={<AddCircleOutlineIcon />} size="small" sx={{ textTransform: 'none', ...FONT, fontWeight: 600 }}>
          Add Tier
        </Button>

        <Divider sx={{ my: 2 }} />

        {/* Multiplier Periods */}
        {sectionTitle('Multiplier Periods')}
        {form.multipliers.length === 0 && (
          <Typography sx={{ fontSize: '0.78rem', color: '#94A3B8', ...FONT, mb: 1 }}>No multiplier periods added.</Typography>
        )}
        {form.multipliers.map((m, idx) => (
          <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
            <DatePicker
              label="Start"
              value={m.startDate}
              onChange={(val) => handleMultiplierChange(idx, 'startDate', val)}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: 'small', sx: { flex: 1 }, InputProps: { sx: FONT }, InputLabelProps: { sx: FONT } } }}
            />
            <DatePicker
              label="End"
              value={m.endDate}
              onChange={(val) => handleMultiplierChange(idx, 'endDate', val)}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: 'small', sx: { flex: 1 }, InputProps: { sx: FONT }, InputLabelProps: { sx: FONT } } }}
            />
            <TextField
              label="Multiplier"
              type="number"
              value={m.multiplier}
              onChange={(e) => handleMultiplierChange(idx, 'multiplier', e.target.value)}
              size="small"
              sx={{ width: 110 }}
              inputProps={{ step: 0.1 }}
              InputProps={{ sx: FONT }}
              InputLabelProps={{ sx: FONT }}
            />
            <IconButton onClick={() => removeMultiplier(idx)} size="small" color="error">
              <RemoveCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button onClick={addMultiplier} startIcon={<AddCircleOutlineIcon />} size="small" sx={{ textTransform: 'none', ...FONT, fontWeight: 600 }}>
          Add Multiplier
        </Button>

        <Divider sx={{ my: 2 }} />

        {/* Caps */}
        {sectionTitle('Caps')}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label={form.metric === 'Revenue' ? 'Minimum Cap (RM)' : 'Minimum Cap (Cases)'}
            type="number"
            value={form.minEligibility}
            onChange={(e) => handleChange('minEligibility', e.target.value)}
            size="small"
            fullWidth
            helperText="Below this, earnings are 0. Leave blank for no minimum."
            InputProps={{ sx: FONT }}
            InputLabelProps={{ sx: FONT }}
          />
          <TextField
            label={form.metric === 'Revenue' ? 'Maximum Cap (RM)' : 'Maximum Cap (Cases)'}
            type="number"
            value={form.maxEligibility}
            onChange={(e) => handleChange('maxEligibility', e.target.value)}
            size="small"
            fullWidth
            helperText="Above this, no further earnings. Leave blank for no cap."
            InputProps={{ sx: FONT }}
            InputLabelProps={{ sx: FONT }}
          />
        </Box>

        {/* Downline Rules */}
        {sectionTitle('Downline Rules')}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
          <TextField label="Max Downline Levels" type="number" value={form.maxDownlineLevels} onChange={(e) => handleChange('maxDownlineLevels', e.target.value)} size="small" fullWidth InputProps={{ sx: FONT }} InputLabelProps={{ sx: FONT }} />
          <TextField label="Min Sales for Downline" type="number" value={form.minSalesForDownline} onChange={(e) => handleChange('minSalesForDownline', e.target.value)} size="small" fullWidth InputProps={{ sx: FONT }} InputLabelProps={{ sx: FONT }} />
          <TextField label="Max Contribution (optional)" type="number" value={form.maxDownlineContribution} onChange={(e) => handleChange('maxDownlineContribution', e.target.value)} size="small" fullWidth InputProps={{ sx: FONT }} InputLabelProps={{ sx: FONT }} />
          <TextField label="Downline Contribution %" type="number" value={form.downlineContributionPercent} onChange={(e) => handleChange('downlineContributionPercent', e.target.value)} size="small" fullWidth InputProps={{ sx: FONT, inputProps: { min: 0, max: 100 } }} InputLabelProps={{ sx: FONT }} />
        </Box>

        {form.metric === 'Revenue' && (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.includeAddOnInRevenue}
                  onChange={(e) => handleChange('includeAddOnInRevenue', e.target.checked)}
                />
              }
              label={
                <Typography sx={{ fontSize: '0.85rem', ...FONT }}>
                  Include <strong>Add-On price</strong> in revenue calculation
                  <Typography component="span" sx={{ display: 'block', fontSize: '0.75rem', color: '#64748B', ...FONT }}>
                    When on, each sale's metric = FinalPrice + AddOnPrice (instead of FinalPrice only).
                  </Typography>
                </Typography>
              }
            />
          </Box>
        )}

      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', ...FONT, color: '#64748B' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !form.name}
          sx={{
            textTransform: 'none',
            ...FONT,
            fontWeight: 600,
            background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)', boxShadow: 'none' },
          }}
        >
          {saving ? 'Saving...' : isEdit ? 'Update Campaign' : 'Create Campaign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
